import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PolishDateInput from '../common/PolishDateInput';

export function parseFeeAmount(value) {
  const text = String(value).trim();
  if (!/^\d{1,6}([,.]\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.replace(',', '.').split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return cents > 0 && cents <= 99999999 ? cents : null;
}

export default function SettlementExtraFeeForm({ month, students, initialFee, correctionRequired, initialReason, onSave, onCancel, idPrefix }) {
  const { t } = useTranslation();
  const tr = key => t(`billing:fees.${key}`);
  const fieldId = name => `${idPrefix}-${name}`;
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [form, setForm] = useState({ description: '',
    category: 'book',
    studentId: '',
    date: todayIso.startsWith(month) ? todayIso : `${month}-01`,
    ...initialFee,
    amount: initialFee ? String(initialFee.amountCents / 100).replace('.', ',') : '',
    reason: initialReason });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState('');
  const inFlight = useRef(false);
  const change = event => { const { name, value } = event.target; setForm(current => ({ ...current, [name]: value })); };
  async function submit(event) {
    event.preventDefault();
    if (inFlight.current) return;
    const invalid = {};
    const amountCents = parseFeeAmount(form.amount);
    if (!form.description.trim() || form.description.trim().length > 200) invalid.description = tr('invalidDescription');
    if (amountCents === null) invalid.amount = tr('invalidAmount');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date) || !form.date.startsWith(`${month}-`) || !Number.isFinite(Date.parse(form.date)) ||
      new Date(form.date).toISOString().slice(0, 10) !== form.date) invalid.date = tr('invalidDate');
    if (correctionRequired && (!form.reason.trim() || form.reason.length > 1000)) invalid.reason = tr('invalidReason');
    setErrors(invalid);
    if (Object.keys(invalid).length) return;
    inFlight.current = true; setBusy(true); setServerError('');
    try {
      await onSave({ description: form.description.trim(), category: form.category, amountCents, date: form.date, studentId: form.studentId || null }, form.reason.trim());
    } catch (error) { setServerError(tr(error.feesRefreshed ? 'conflict' : 'saveError')); } finally { inFlight.current = false; setBusy(false); }
  }
  const field = (name, label, content) => (
    <div className="col-md-4 form-group">
      <label htmlFor={fieldId(name)}>{label}</label>
      {content || (
      <input
        id={fieldId(name)}
        name={name}
        className={`form-control${errors[name] ? ' is-invalid' : ''}`}
        aria-invalid={!!errors[name]}
        aria-describedby={errors[name] ? fieldId(`${name}-error`) : undefined}
        inputMode={name === 'amount' ? 'decimal' : undefined}
        value={form[name]}
        onChange={change}
      />
      )}
      {errors[name] && <div id={fieldId(`${name}-error`)} className="text-danger small" role="alert">{errors[name]}</div>}
    </div>
  );
  return (
    <form onSubmit={submit} className="border rounded p-3 mt-2 settlement-fee-form">
      <div className="row">
        {field('description', tr('description'))}
        {field(
          'category',
          tr('category'), (
            <select id={fieldId('category')} name="category" className="form-control" value={form.category} onChange={change}>
              {['book', 'materials', 'registration', 'other'].map(category => <option key={category} value={category}>{tr(category)}</option>)}
            </select>),
        )}
        {field('amount', tr('amount'))}
        {field(
          'studentId',
          tr('student'), (
            <select id={fieldId('studentId')} name="studentId" className="form-control" value={form.studentId || ''} onChange={change}>
              <option value="">{tr('wholeClient')}</option>{students.map(student => <option key={student.id} value={student.id}>{student.firstName} {student.lastName}</option>)}
              {initialFee && initialFee.studentId && !students.some(student => student.id === initialFee.studentId) &&
              <option value={initialFee.studentId}>{initialFee.studentNameSnapshot}</option>}
            </select>),
        )}
        <div className="col-md-4"><PolishDateInput name="date" label={tr('date')} value={form.date} onChange={change} onBlur={() => {}} error={errors.date} /></div>
        <div className="col-md-4 form-group"><label htmlFor={fieldId('month')}>{tr('month')}</label><input id={fieldId('month')} className="form-control" value={month} readOnly /></div>
        {correctionRequired && field('reason', tr('reason'))}
      </div>
      {serverError && <div role="alert" className="alert alert-danger">{serverError}</div>}
      <button type="button" className="btn manage-teal-button mr-2 mb-2" onClick={onCancel} disabled={busy}>{tr('cancel')}</button>
      <button type="submit" className="btn manage-red-button mb-2" disabled={busy}>{tr(busy ? 'saving' : 'save')}</button>
    </form>
  );
}
SettlementExtraFeeForm.propTypes = { month: PropTypes.string.isRequired,
  students: PropTypes.array.isRequired,
  initialFee: PropTypes.object,
  correctionRequired: PropTypes.bool,
  initialReason: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  idPrefix: PropTypes.string };
SettlementExtraFeeForm.defaultProps = { initialFee: null, correctionRequired: false, initialReason: '', idPrefix: 'fee' };
